import { signInWithAppleOAuth } from '@/lib/oauth/google-oauth';
import { useAuthStore } from '@/store/authStore';
import { showToast } from '@/store/toastStore';
import { sanitizeErrorText } from '@/utils/api-error';
import { useCallback, useState } from 'react';

type UseAppleOAuthOptions = {
  onError?: (error: unknown) => void;
  /**
   * Quando presente, recebe o token do Supabase em vez de trocar a sessão:
   * é assim que a conta logada vincula o Apple sem virar outra sessão.
   */
  onToken?: (token: string) => Promise<void>;
};

export function useAppleOAuth(options?: UseAppleOAuthOptions) {
  const appleLogin = useAuthStore((state) => state.appleLogin);
  const [isAppleLoading, setIsAppleLoading] = useState(false);

  const signInWithApple = useCallback(async () => {
    try {
      setIsAppleLoading(true);
      const outcome = await signInWithAppleOAuth();

      if (outcome.status === 'success') {
        if (options?.onToken) {
          await options.onToken(outcome.accessToken);
        } else {
          await appleLogin(outcome.accessToken);
        }
        return;
      }

      if (outcome.status === 'error') {
        showToast(
          sanitizeErrorText(outcome.message, 'Não foi possível entrar. Tente novamente.'),
          'error',
        );
        options?.onError?.(new Error(outcome.message));
        return;
      }

      if (outcome.status === 'no_tokens') {
        showToast('Não foi possível entrar. Tente novamente.', 'error');
        return;
      }

      if (outcome.status === 'cancelled') {
        showToast('Login com Apple cancelado.', 'info');
      }
    } catch (error) {
      console.error('Apple OAuth Error:', error);
      showToast('Falha ao tentar logar com Apple!', 'error');
      options?.onError?.(error);
    } finally {
      setIsAppleLoading(false);
    }
  }, [appleLogin, options]);

  return { signInWithApple, isAppleLoading };
}
